import { EFinancingProgram, EProvince } from '@if/core/types'
import { formatCurrency, formatDate, normalizeNumber } from '@if/core/utils'
import {
  ActionButton,
  Banner,
  BodyText,
  ControlledCheckbox,
  FormContainer,
  Icon,
  PageTitle,
  SectionTitle,
  useFormContainerContext,
  useValidatedWatch,
} from '@if/mui'
import { Box, Grid, IconButton, Stack, Tooltip } from '@mui/material'
import { useMerchantFinancingConfig } from '@src/data/api/configs-api/financing-config-api'
import {
  useGetIfXpressFunding,
  usePostIfXpressWorksheet,
  usePutIfXpressWorksheet,
} from '@src/data/api/worksheet-api/ifxpress-worksheet-api'
import { useGetPossibleTermsIfXpress } from '@src/data/api/worksheet-api/worksheet-api'
import {
  createNewIfXpressWorksheetDto,
  getIfXpressFundingDto,
  isVariableInterest,
  updateIfXpressWorksheet,
} from '@src/data/worksheet-selectors'
import { Constants, FinancingProgramConfigs } from '@src/types/Constants'
import { CreditApplication } from '@src/types/CreditApplicationSchema'
import { Merchant } from '@src/types/Merchant'
import {
  EditIfXpressWorksheetDto,
  buildEditIfXpressWorksheetDtoSchema,
  getMaxTermForAmount,
} from '@src/types/WorksheetSchema'
import { Info } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { type DefaultValues, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useLoanTermSelection } from '../../ViewCreditApplicationPage/worksheet-hooks'
import WorksheetSkeleton from '../components/WorksheetSkeleton'
import IfXpressFundingSummary from './components/IfXpressFundingSummary'
import WorksheetDetails from './components/IfXpressWorksheetDetails'

interface Props {
  merchant: Merchant
  creditApplication: CreditApplication
  merchantPayments: { label: string; value: string; id: string }[]
}

interface BodyProps extends Props {
  /** Save (post/put) in flight — drives the submit button's spinner. */
  isSaving: boolean
  minTerm: number
  maxTerm: number
  longTerm: number
  largeLoanAmount: number
}

// The worksheet body: reads the form off the FormContainer context (so all the
// watch-driven funding computation lives here), renders the inputs + funding
// summary, and submits via the `type="submit"` action buttons.
const IfXpressWorksheetBody = ({
  merchant,
  creditApplication,
  merchantPayments,
  isSaving,
  minTerm,
  maxTerm,
  longTerm,
  largeLoanAmount,
}: BodyProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { methods } = useFormContainerContext<EditIfXpressWorksheetDto>()
  const { setValue, control } = methods

  const [openInsuranceInfoBox, setOpenInsuranceInfoBox] = useState(false)
  const deliveryOn = useWatch({ control, name: 'deliveryOn' })
  const amountRequested = normalizeNumber(useWatch({ control, name: 'amountRequested' })?.toString()) ?? 0
  const { value: settledAmount, isValid: isAmountValid } = useValidatedWatch({
    control,
    name: 'amountRequested',
    debounceTime: Constants.AmountRequestedDebounceMs,
  })
  const debouncedAmountRequested = isAmountValid ? (normalizeNumber(settledAmount?.toString()) ?? 0) : 0
  const isAmountOutOfRange = !!settledAmount && !isAmountValid
  const paymentFrequency = useWatch({ control, name: 'paymentFrequency' }) ?? 'monthly'
  const term = useWatch({ control, name: 'term' })
  const firstPaymentOn = useWatch({ control, name: 'firstPaymentOn' })
  const paymentPlanId = useWatch({ control, name: 'paymentPlanId' })
  const includeInsurance = useWatch({ control, name: 'includeInsurance' })
  const hasVariableInterest = isVariableInterest(paymentPlanId)
  const [error, setError] = useState<Error | null>(null)

  const dto = useMemo(() => {
    return getIfXpressFundingDto(
      creditApplication.id,
      debouncedAmountRequested,
      paymentFrequency,
      term,
      formatDate(deliveryOn),
      formatDate(firstPaymentOn),
      includeInsurance,
      creditApplication.applicant.currentAddress.stateIso,
      paymentPlanId,
      creditApplication.finalCreditDecision.interestRate,
      creditApplication.coApplicant !== null,
    )
  }, [
    creditApplication,
    debouncedAmountRequested,
    deliveryOn,
    firstPaymentOn,
    includeInsurance,
    paymentFrequency,
    paymentPlanId,
    term,
  ])
  const computeMonthTermFrequency = useMemo(
    () => ({
      merchantId: creditApplication.merchantId,
      paymentPlanId,
      interestRate: creditApplication.finalCreditDecision.interestRate,
      hasCoapplicant: creditApplication.coApplicant !== null,
      stateIso: creditApplication.applicant.currentAddress.stateIso!,
      paymentFrequency,
      deliveryOn: formatDate(deliveryOn),
      firstPaymentOn: formatDate(firstPaymentOn),
      maxPmtAmount: creditApplication.finalCreditDecision.maxPmtAmount,
      amountRequested: debouncedAmountRequested,
      includeInsurance,
      include84Months: (creditApplication.finalCreditDecision.maxAmountFinanced ?? 0) >= largeLoanAmount,
    }),
    [
      creditApplication.merchantId,
      paymentPlanId,
      creditApplication.finalCreditDecision.interestRate,
      creditApplication.coApplicant,
      creditApplication.applicant.currentAddress.stateIso,
      paymentFrequency,
      deliveryOn,
      firstPaymentOn,
      creditApplication.finalCreditDecision.maxPmtAmount,
      creditApplication.finalCreditDecision.maxAmountFinanced,
      debouncedAmountRequested,
      includeInsurance,
      largeLoanAmount,
    ],
  )

  const [possibleTerms, isLoadingTerms] = useGetPossibleTermsIfXpress(computeMonthTermFrequency)
  const { loanTermChoicesList, handlePaymentPlanChange } = useLoanTermSelection({
    possibleTerms,
    creditApplication,
    merchant,
    paymentPlanId,
    term,
    regularMinTerm: minTerm,
    maxTermForAmount: getMaxTermForAmount(
      { ...FinancingProgramConfigs[EFinancingProgram.IfXpress], maxTerm, longTerm, largeLoanAmount },
      normalizeNumber(settledAmount?.toString()) ?? 0,
    ),
    setValue,
  })

  const [fetchedIfXpressFunding, isComputing] = useGetIfXpressFunding(creditApplication.id, dto)
  const computedIfXpressFunding = debouncedAmountRequested ? fetchedIfXpressFunding : null
  const doesTermSupportInsurance = useMemo(() => {
    if (!possibleTerms?.monthlyTerms) return false
    const matchingTerm = possibleTerms.monthlyTerms.find((item) => item.term === Number(term))

    return matchingTerm ? matchingTerm.effectiveRateWithInsuranceIsLegal : false
  }, [possibleTerms, term])

  useEffect(() => {
    if (
      term === longTerm &&
      (creditApplication?.finalCreditDecision.maxAmountFinanced ?? 0) < largeLoanAmount &&
      creditApplication?.finalCreditDecision.maxTermDuration
    )
      setValue('term', creditApplication?.finalCreditDecision.maxTermDuration)
  }, [
    creditApplication?.finalCreditDecision.maxAmountFinanced,
    term,
    longTerm,
    largeLoanAmount,
    creditApplication?.finalCreditDecision.maxTermDuration,
    setValue,
  ])

  const isAPRTooHigh = useMemo(() => {
    const address = creditApplication.applicant.currentAddress
    if (!address || !computedIfXpressFunding) return false

    const { effectiveRate } = computedIfXpressFunding
    const { stateIso } = address

    const aprThresholds: Record<string, number> = {
      [EProvince.Newfoundland]: 27,
    }

    const threshold = aprThresholds[stateIso!]

    return threshold !== undefined && effectiveRate >= threshold
  }, [computedIfXpressFunding, creditApplication.applicant.currentAddress])

  const ifXpressPayment = computedIfXpressFunding?.paymentForFrequency
  useEffect(() => {
    if (ifXpressPayment !== undefined && ifXpressPayment > computeMonthTermFrequency?.maxPmtAmount) {
      setError(new Error(t('common.errors.maxpaymentExceedsMaximumAllowed')))
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }
    setError(null)
  }, [ifXpressPayment, computeMonthTermFrequency?.maxPmtAmount, t])

  return (
    <div>
      {error && (
        <Banner severity="error" title={t('common.error')} sx={{ width: '100%', mb: 4 }}>
          {error.message}
        </Banner>
      )}
      <Stack direction={{ md: 'column', xs: 'column-reverse' }} spacing={5} justifyContent="space-between">
        <Stack
          direction={{ sm: 'row', xs: 'column' }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ sm: 'self-end', xs: 'self-start' }}
        >
          <Stack direction="column" justifyContent="space-between">
            <PageTitle>{t('worksheet.worksheet')}</PageTitle>
            <SectionTitle>
              {t('worksheet.maxLoanAmount')}: {formatCurrency(creditApplication.finalCreditDecision.maxAmountFinanced)}
            </SectionTitle>
          </Stack>
          <Stack direction="row" spacing={2} display="flex">
            <ActionButton emphasis="principal" onClick={() => navigate(-1)}>
              {t('common.previous')}
            </ActionButton>
            <ActionButton
              type="submit"
              emphasis="principal"
              disabled={isAPRTooHigh || isComputing || !computedIfXpressFunding || error != null}
              loading={isSaving}
            >
              {t('worksheet.createContract')}
            </ActionButton>
          </Stack>
        </Stack>

        <Stack direction={{ md: 'row', xs: 'column-reverse' }} spacing={5} justifyContent="space-between">
          <Box sx={{ flex: 1 }}>
            <Grid container spacing={2}>
              <Grid size={12}>
                <WorksheetDetails
                  creditApplication={creditApplication}
                  merchantPayments={merchantPayments}
                  loanTermChoicesList={loanTermChoicesList}
                  isLoadingTerm={isLoadingTerms}
                  planPromoRules={merchant.planPromoRules}
                  paymentFrequency={paymentFrequency}
                  onPaymentPlanChange={handlePaymentPlanChange}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                  md: 4,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <ControlledCheckbox
                    name="includeInsurance"
                    control={control}
                    label={t('worksheet.addProtectionPlan')}
                    disabled={!doesTermSupportInsurance}
                  />
                  <Tooltip title={t('worksheet.includeProtectionPlan')}>
                    <IconButton onClick={() => setOpenInsuranceInfoBox((prev) => !prev)}>
                      <Icon icon={Info} size="sm" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Grid>

              {openInsuranceInfoBox && (
                <Box sx={{ p: 1.25, ml: 2 }}>
                  <SectionTitle sx={{ mb: 1.25 }}>{t('worksheet.insuranceIncentive')}</SectionTitle>
                  <Box component="ul" sx={{ pl: 2.5 }}>
                    <li>
                      <BodyText sx={{ mb: 1.25 }}>{t('worksheet.lifeInsurance')}</BodyText>
                    </li>
                    <li>
                      <BodyText sx={{ mb: 1.25 }}>{t('worksheet.accidentalDisability')}</BodyText>
                    </li>
                  </Box>
                </Box>
              )}
              <Grid container sx={{ display: { md: 'none' } }} size={12}>
                <Grid
                  size={{
                    xs: 12,
                    sm: 6,
                  }}
                >
                  <ActionButton fullWidth tone="destructive" onClick={() => navigate(-1)}>
                    {t('common.previous')}
                  </ActionButton>
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    sm: 6,
                  }}
                >
                  <ActionButton
                    type="submit"
                    fullWidth
                    emphasis="principal"
                    disabled={isAPRTooHigh || isComputing || !computedIfXpressFunding}
                    loading={isSaving}
                  >
                    {t('worksheet.createContract')}
                  </ActionButton>
                </Grid>
              </Grid>
            </Grid>
          </Box>
          <Box
            sx={{
              border: 1,
              padding: 2,
              maxWidth: { xs: '100%', md: '350px' },
            }}
          >
            <IfXpressFundingSummary
              creditApplication={creditApplication}
              amountRequested={amountRequested}
              hasVariableInterest={hasVariableInterest}
              firstPaymentOn={formatDate(firstPaymentOn)}
              merchant={merchant}
              paymentPlanId={paymentPlanId}
              computedIfXpressFunding={computedIfXpressFunding}
              isAmountOutOfRange={isAmountOutOfRange}
              isComputing={isComputing}
            />
          </Box>
        </Stack>
      </Stack>
    </div>
  )
}

const IfXpressWorksheet = ({ merchant, creditApplication, merchantPayments }: Props) => {
  const navigate = useNavigate()

  const [merchantFinancingConfig, isLoadingConfig] = useMerchantFinancingConfig(
    merchant?.id,
    EFinancingProgram.IfXpress,
  )
  const defaultConfig = FinancingProgramConfigs[EFinancingProgram.IfXpress]
  const minTerm = merchantFinancingConfig?.minLoanTerm ?? defaultConfig.minTerm
  const maxTerm = merchantFinancingConfig?.maxLoanTerm ?? defaultConfig.maxTerm
  const longTerm = merchantFinancingConfig?.longTerm ?? defaultConfig.longTerm
  const largeLoanAmount = merchantFinancingConfig?.largeLoanAmount ?? defaultConfig.largeLoanAmount
  const configOverride = { minTerm, maxTerm, longTerm, largeLoanAmount }

  const EditIfXpressWorksheetDtoSchema = buildEditIfXpressWorksheetDtoSchema(
    creditApplication.applicant.hardHitReportReceivedOn!,
    creditApplication.finalCreditDecision.maxAmountFinanced!,
    creditApplication.merchantPaymentPlanId,
    configOverride,
  )

  // Cast the loaded worksheet through the schema so its wire-string dates become
  // `DateOnly` (what the pickers require) — symmetric with the create branch's
  // `getDefault()`, which already yields `DateOnly`.
  const defaultValues = creditApplication.worksheet
    ? EditIfXpressWorksheetDtoSchema.cast(creditApplication.worksheet)
    : EditIfXpressWorksheetDtoSchema.getDefault()

  const [postWorksheet, isPosting] = usePostIfXpressWorksheet()
  const [putWorksheet, isPuting] = usePutIfXpressWorksheet()

  const navigateToView = (financingProgramId: string, id: string) => {
    navigate(`/credits/${financingProgramId}/${id}`)
  }

  const saveWorksheet = async (formData: EditIfXpressWorksheetDto) => {
    if (creditApplication.worksheet == null) {
      await postWorksheet({ worksheet: createNewIfXpressWorksheetDto(creditApplication.id, formData) })
    } else {
      await putWorksheet({ worksheet: updateIfXpressWorksheet(formData, creditApplication) })
    }
    // Only reached when the mutation resolved; a failure rejects above and surfaces
    // through FormContainer's onSubmit error handling.
    navigateToView(creditApplication.financingProgramId, creditApplication.id)
  }

  if (isLoadingConfig) {
    return <WorksheetSkeleton />
  }

  return (
    <FormContainer
      mode="create"
      useFormSetup={() => ({
        schema: EditIfXpressWorksheetDtoSchema,
        defaultValues: defaultValues as unknown as DefaultValues<EditIfXpressWorksheetDto>,
      })}
      successMessage={false}
      hideActions
      onSubmit={async (formData) => {
        // Let a save failure propagate so FormContainer surfaces it (its error path).
        await saveWorksheet(formData)
      }}
    >
      <IfXpressWorksheetBody
        merchant={merchant}
        creditApplication={creditApplication}
        merchantPayments={merchantPayments}
        isSaving={isPosting || isPuting}
        minTerm={minTerm}
        maxTerm={maxTerm}
        longTerm={longTerm}
        largeLoanAmount={largeLoanAmount}
      />
    </FormContainer>
  )
}

export default IfXpressWorksheet
